import { identity } from 'lodash-es';
import { filter, map, pipe, scan } from 'rxjs';
import type { MinimalSubscriptionResponse } from '../types/SubscriptionResponse';
import { logger } from '../utils';
import { compareTimestampsWithMicrosecondPrecision } from '../utils/microseconds';
import { isRemoveSubscriptionUpdate } from './utils';
type RelevancePredicate<T, TRelevant extends T> = ((item: T) => boolean) | ((item: T) => item is TRelevant);

export interface WSScanToMapParams<T, KeyType extends string | number, P, TRelevant extends T = T> {
  /** The unique key to use for storing items. The type of the returned key must be `(string | number)` */
  getUniqueKey: (item: T) => KeyType;
  /** An optional function going from `T => P` where you determine what you would like stored as values in the map.
   * @example
   * // basic case: just store T. This will emit a map of type `Map<KeyType, T>`
   * getInsertable: item => item
   * // store a specific property of T, eg DisplayName. This will emit a map of type `Map<KeyType, string>`
   * getInsertable: item => item.DisplayName
   * @default lodash.identity
   */
  getInsertable?: (item: TRelevant) => P;
  /** Whether or not to create a new map on each update. Necessary in order to trigger change detection if this operator is
   * the last transformation operator before bringing the variable into React-land. For example, if this operator is last in a
   * `useObservableValue` usage, you should most likely set this to `true`. If it's in the middle of a longer array of pipe transformations, set it as `false`.
   * This option does not default to false as it is important that the developer is aware of the implications of this option.
   * This option also make us treat all the maps as immutable
   */
  newMapEachUpdate: boolean;
  /**
   * A callback which allows you to decide whether or not the given item should be deleted. Will be invoked for every entry in every update.
   * This callback takes full precendence -- if provided, you handle all deletion of entries and no other deletion will take place.
   * As in: if provided, the pipe won't check UpdateAction.Remove for you.
   */
  shouldDelete?: (item: T, message: MinimalSubscriptionResponse<T>) => boolean;
  /**
   * Whether an update is relevant to this index. Irrelevant updates are ignored
   * unless their key already exists in the map, in which case shouldDelete can
   * remove the stale entry. This supports updates where an entry's relevant
   * properties change and it should leave the index. When provided, unchanged
   * messages do not emit.
   */
  isRelevant?: RelevancePredicate<T, TRelevant>;
  /**
   * If set to true, will only allow updates if the `Revision` property is greater on the updated entry compared to the existing entry in the map.
   * Assumes that there is a `Revision` property on the incoming type T and the inserted type P.
   */
  ensureRevisionOrdering?: boolean;
  /**
   * If set to true, will only allow updates if the `Timestamp` property is later on the updated entry compared to the existing entry in the map.
   * Assumes that there is a `Timestamp` property on the incoming type T and the inserted type P.
   */
  ensureTimestampOrdering?: boolean;
  /**
   * If true, will wait for all pages to load before emitting the map. If false, will emit the map as soon as the first page is loaded.
   */
  waitForAllPagesToLoad?: boolean;
}

/**
 * A pipe which scans received updates to a map.
 * Handles remove update actions
 * Uses the getUniqueKey function you pass in to get the key used for storing in the map
 * @returns a map with unique entries
 */
export function wsScanToMap<T extends AnyObject, KeyType extends string | number, P = T, TRelevant extends T = T>({
  getUniqueKey,
  getInsertable = identity,
  newMapEachUpdate,
  shouldDelete,
  isRelevant,
  ensureRevisionOrdering,
  ensureTimestampOrdering,
  waitForAllPagesToLoad = false,
}: WSScanToMapParams<T, KeyType, P, TRelevant>) {
  const isRelevantUpdate = (update: T): update is TRelevant => isRelevant?.(update) ?? true;

  return pipe(
    scan(
      ({ map: acc }, json: MinimalSubscriptionResponse<T>) => {
        let map = json.initial ? new Map<KeyType, P>() : acc;
        let changed = !!json.initial;
        let didChange = !!json.initial;

        const getWritableMap = () => {
          if (newMapEachUpdate && !changed) {
            map = new Map(map);
            changed = true;
          }
          return map;
        };

        // Be defensive against inconsistent API responses that omit data.
        if (json.data == null) {
          return { map, isLoading: !!json.next, didChange };
        }

        for (const update of json.data) {
          const key = getUniqueKey(update);
          if (key === undefined) {
            continue;
          }

          const existingEntry = map.get(key);
          const updateIsRelevant = isRelevantUpdate(update);
          if (!updateIsRelevant && existingEntry == null) {
            continue;
          }

          if (existingEntry) {
            if (ensureRevisionOrdering) {
              if (!(typeof existingEntry === 'object') || !('Revision' in existingEntry) || !('Revision' in update)) {
                logger.error(
                  new Error(
                    'ensureRevisionOrdering is set to true, but Revision is not present on the existing or update object'
                  ),
                  { existingEntry, update }
                );
              }

              // @ts-expect-error - needs stronger typing clarity around Revision
              const existingRevision = existingEntry['Revision'] as number | undefined;
              // @ts-expect-error - needs stronger typing clarity around Revision
              const updateRevision = update['Revision'] as number | undefined;
              if (existingRevision != null && updateRevision != null && updateRevision < existingRevision) {
                continue;
              }
            }

            if (ensureTimestampOrdering) {
              if (!(typeof existingEntry === 'object') || !('Timestamp' in existingEntry) || !('Timestamp' in update)) {
                logger.error(
                  new Error(
                    'ensureTimestampOrdering is set to true, but Timestamp is not present on the existing or update object'
                  ),
                  { existingEntry, update }
                );
              }

              const existingTimestamp = existingEntry['Timestamp' as keyof typeof existingEntry];
              const updateTimestamp = update['Timestamp' as keyof typeof update];
              if (
                typeof existingTimestamp === 'string' &&
                typeof updateTimestamp === 'string' &&
                // -1 indicates that updateTimestamp is earlier than time than existingTimestamp
                compareTimestampsWithMicrosecondPrecision(updateTimestamp, existingTimestamp) === -1
              ) {
                continue;
              }
            }
          }

          // If specified, the shouldDelete function has full control over _all_ deletion
          const shouldDeleteUpdate = shouldDelete
            ? shouldDelete(update, json)
            : isRemoveSubscriptionUpdate(update, json);
          if (shouldDeleteUpdate) {
            if (map.has(key)) {
              getWritableMap().delete(key);
              didChange = true;
            }
          } else {
            if (!updateIsRelevant) {
              continue;
            }
            getWritableMap().set(key, getInsertable(update));
            didChange = true;
          }
        }

        return { map, isLoading: !!json.next, didChange };
      },
      { map: new Map<KeyType, P>(), isLoading: false, didChange: false }
    ),
    filter(({ isLoading, didChange }) => (!isLoading || !waitForAllPagesToLoad) && (isRelevant == null || didChange)),
    map(({ map }) => map)
  );
}
